import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTable, useFilters, useGlobalFilter, useSortBy, usePagination } from 'react-table';
import isEmpty from 'lodash/isEmpty';
import { isMobile } from 'react-device-detect';
import usePortal from 'react-useportal';
import SearchTableFilter from '../../SearchTableFilter';
import SearchPlayerCard from './SearchPlayerCard';
import useNarrowPane from '../../../../../hooks/useNarrowPane';
import {StyledCardList} from '../../../../Team/components/TableContainer/components/Table/squadCardStyled';
import RatingCell from '../../RatingCell';
import PotentialCell from '../../PotentialCell';
import ActionCell from '../../ActionCell';
import NameCell from '../../NameCell';
import TeamOwnerIcon from '../../TeamOwnerIcon';
import { ScoutPopup, SignFreeAgentPopup, TeamInfoPopup, ContractOfferPopup } from '../../../../../components/Popup';

import {
  showScoutPopupVisibility,
  transferSetUserWithOfferToBuy,
  transferShowUnshowFilter,
  setDataForCardRequest,
} from '../../../../../redux/actions/Transfers';
import { showPopupWindow } from '../../../../../redux/actions/Popups';
import { teamAddToList } from '../../../../../redux/actions/Team';

import { leagueSetTeamInfo } from '../../../../../redux/actions/League';

import { dataForSidebar } from '../../../../../redux/selector/Sidebar';
import { getPopupsList, getPopupByName } from '../../../../../redux/selector/Popups';

import { playerFromList } from '../../../../../redux/selector/Team';
import { isAuth } from '../../../../../redux/selector/User';
import { leagueTable, isTeamListLoaded } from '../../../../../redux/selector/League';

// easy to create sorter by this
// how to make sorting by range - https://codesandbox.io/s/github/tannerlinsley/react-table/tree/master/examples/filtering
import { StyledTable, StyledTableContainer, StyledThead, StyledTbody, StyledTh, StyledTd, StyledTrTh, StyledTrTd, StyledSorted, StyledPagination, ArrowRight, ArrowLeft, StyledPaginationButton, StyledPageNumber, StyledPageLabel, StyledUnderlineTeam, StyledNameContainer, StyledTeamCell } from './styled';

import { POPUPS } from '../../../../../service/constants';
import { simplifiedPosition } from '../../../../../service/data';
import { clubOwnerKind } from '../../../../../service/clubOwnerKind';
import { goToDiscordLogin } from '../../../../../service/functions';
import DefaultColumnFilter, {
  isInRange,
} from '../../../../../components/TableFilters/DefaultColumnFilter';
import exactValueFilter from '../../../../../components/TableFilters/exactValueFilter';

const ADVANCED_FILTER_COLUMNS = [
  'fav_style',
  'fav_mindset',
  'worst_mindset',
  'fav_opponent',
  'worst_opponent',
  'life_stage',
];

const { SCOUT_POPUP, FREE_AGENT_POPUP, TRANSFER_SEARCH_POPUP, LOAN_OFFER_POPUP, TEAM_INFO_POPUP, CONTRACT_OFFER_POPUP
} = POPUPS;

export default React.memo(function Table({ columns, data, getUserToSell }) {
  const [clicked, setClicked] = useState(null);
  const narrow = useNarrowPane();
  const dispatch = useDispatch();
  React.useEffect(() => () => dispatch(transferShowUnshowFilter(false)), [dispatch]);
  const freeAgentPopupData = useSelector(getPopupByName(POPUPS.FREE_AGENT_POPUP));
  const contractOfferPopupData = useSelector(getPopupByName(CONTRACT_OFFER_POPUP));
  const popupData = useSelector(getPopupsList);
  const dataForCash = useSelector(dataForSidebar);

  const teamInfoPopupData = useSelector(getPopupByName(TEAM_INFO_POPUP));
  const selectedPlayerFromTable = useSelector(playerFromList);
  const isAuthenticated = useSelector(isAuth);
  const leagueTeams = useSelector(leagueTable);
  const listedTeams = useSelector(isTeamListLoaded);
  const botTeamIds = useMemo(() => {
    const ids = new Set();
    const mark = (club) => {
      if (
        club?._id &&
        (club.bot === 1 || club.bot === true || club.bot === '1')
      ) {
        ids.add(club._id);
      }
    };
    (Array.isArray(leagueTeams) ? leagueTeams : []).forEach(mark);
    (Array.isArray(listedTeams) ? listedTeams : []).forEach(mark);
    return ids;
  }, [leagueTeams, listedTeams]);

  const customRange = useMemo(
    () => (rows, id, filterValue) =>
      rows.filter((row) => {
        const rowValue = row.values[id];

        return rowValue !== undefined ? isInRange(rowValue, filterValue) : false;
      }),
    []
  );
  customRange.autoRemove = (val) => !val;
  const filterTypes = useMemo(
    () => ({
      // Or, override the default text filter to use
      // "startWith"
      ageRange: customRange,
      equals: exactValueFilter,
      text: (rows, id, filterValue) =>
        rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
            : true;
        }),
    }),
    []
  );

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    allColumns,
    setSortBy,
    // Get the state from the instance
    state: { pageIndex, filters, sortBy },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
      getRowId: (row, relativeIndex) => String(row._id || relativeIndex),
      initialState: {
        pageIndex: 0,
        pageSize: isMobile ? 15 : 50,
        hiddenColumns: ADVANCED_FILTER_COLUMNS,
      },
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetPage: false,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const mobileSortOptions = [{value:'default',label:'Default order'}, ...allColumns.filter(column=>column.canSort && !ADVANCED_FILTER_COLUMNS.includes(column.id)).flatMap(column=>[
    {value:column.id+':asc',label:column.Header+' ↑',id:column.id,desc:false},
    {value:column.id+':desc',label:column.Header+' ↓',id:column.id,desc:true},
  ])];
  const showScoutModal = (cell) => {
    const { _id } = cell.row.original;
    dispatch(showScoutPopupVisibility(_id));
    dispatch(showPopupWindow(_id, SCOUT_POPUP, false));
  };
  const showSignModal = (cell) => {
    const { transfer, newOffer, teamName, _id } = cell.row.original;
    if (transfer === undefined && !newOffer && teamName !== 'Free agent') {
      dispatch(showPopupWindow(_id, TRANSFER_SEARCH_POPUP, false));
    }
    if (teamName === 'Free agent') {
      dispatch(showPopupWindow(_id, CONTRACT_OFFER_POPUP, false));
    }
  };

  const showLoanModal = (cell) => {
    const { first_name, last_name, _id } = cell.row.original;
    dispatch(
      setDataForCardRequest({
        id: _id,
        name: `${first_name} ${last_name}`,
        transfer_fee_offer: null,
      })
    );
    dispatch(showPopupWindow(LOAN_OFFER_POPUP, LOAN_OFFER_POPUP, false));
  };

  const contractPlayer = useMemo(() => {
    if (!contractOfferPopupData?.id) return null;
    return data.find((player) => player._id === contractOfferPopupData.id) || null;
  }, [data, contractOfferPopupData]);

  const { Portal } = usePortal({
    bindTo: document && document.querySelector('#modal'),
  });
  return (
    <>
      {popupData[SCOUT_POPUP] && (
        <Portal>
          <ScoutPopup id={popupData[SCOUT_POPUP].id} popupName={SCOUT_POPUP} cash={dataForCash.cash} />
        </Portal>
      )}
      {contractOfferPopupData && (
        <Portal>
          <ContractOfferPopup
            key={contractOfferPopupData.id}
            isExtended={false}
            popupName={CONTRACT_OFFER_POPUP}
            id={contractOfferPopupData.id}
            isFreeAgentPopup
            salary={contractPlayer?.salary}
          />
        </Portal>
      )}
      {!isEmpty(freeAgentPopupData) && (
        <Portal>
          <SignFreeAgentPopup
            id={freeAgentPopupData.id}
            popupName={FREE_AGENT_POPUP}
            cash={dataForCash.cash}
            deselectClicked={() => {
              setClicked(null);
            }}
          />
        </Portal>
      )}

      {!isEmpty(teamInfoPopupData) && (
        <Portal>
          <TeamInfoPopup popupName={TEAM_INFO_POPUP} />
        </Portal>
      )}
      <SearchTableFilter allColumns={allColumns} filters={filters} inline={narrow} sortOptions={mobileSortOptions} sortValue={mobileSortOptions.find(option=>option.value===(sortBy.length?sortBy[0].id+':'+(sortBy[0].desc?'desc':'asc'):'default'))} onSortChange={option=>setSortBy(option?.id?[{id:option.id,desc:option.desc}]:[])} />
      {narrow ? <StyledCardList>{page.map(row=>{
        prepareRow(row);
        const player=row.original,cell={row};
        const action=callback=>{if(!isAuthenticated){goToDiscordLogin();return;}dispatch(transferSetUserWithOfferToBuy(player));callback(cell);};
        return <SearchPlayerCard key={row.id} row={row} selected={clicked===row.id}
          ownerKind={clubOwnerKind({...player,botTeamIds})}
          onSelect={()=>{dispatch(teamAddToList(player));setClicked(row.id);getUserToSell(player);}}
          onClub={()=>{dispatch(leagueSetTeamInfo(player.team,false));dispatch(showPopupWindow(TEAM_INFO_POPUP,TEAM_INFO_POPUP,true));}}
          actions={<ActionCell cell={cell} openSignPopup={()=>action(showSignModal)} openLoanPopup={()=>action(showLoanModal)} openScoutPopup={()=>action(showScoutModal)}/>}
        />;
      })}{page.length===0 && <p>No players found.</p>}</StyledCardList> : (
      <StyledTableContainer>
        <StyledTable {...getTableProps()}>
          <StyledThead>
            {headerGroups.map((headerGroup) => (
              <StyledTrTh {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <StyledTh type={column.Header} {...column.getHeaderProps(column.getSortByToggleProps({ title: '' }))}>
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (
                        <React.Fragment>
                          {column.isSortedDesc ? (
                            <StyledSorted type="fromMaxToMin" />
                          ) : (
                            <StyledSorted type="fromMinToMax" />
                          )}
                        </React.Fragment>
                      ) : (
                        <StyledSorted />
                      )}
                    </span>
                  </StyledTh>
                ))}
              </StyledTrTh>
            ))}
          </StyledThead>
          <StyledTbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <StyledTrTd className={`row-number-${row.id} search-cell`} clicked={clicked} {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <StyledTd
                      onClick={(e) => {
                        if (selectedPlayerFromTable?._id !== cell.row.original._id) {
                          dispatch(teamAddToList(cell.row.original));
                        }
                        setClicked(row.id);
                        if (cell.column.Header === 'Team') {
                          if (!isEmpty(cell.row.original.team)) {
                            e.stopPropagation();
                            e.nativeEvent.stopImmediatePropagation();
                            dispatch(leagueSetTeamInfo(cell.row.original.team, false));
                            dispatch(showPopupWindow(TEAM_INFO_POPUP, TEAM_INFO_POPUP, true));
                          }
                        }
                        if (cell.column.Header === 'Actions') {
                          e.stopPropagation();
                          e.nativeEvent.stopImmediatePropagation();
                          dispatch(transferSetUserWithOfferToBuy(cell.row.original));
                          return false;
                        }
                        if (!isEmpty(e) && (e.target.name === 'scoutCell' || e.target.name === 'signCell')) {
                          dispatch(transferSetUserWithOfferToBuy(cell.row.original));
                          e.stopPropagation();
                          e.nativeEvent.stopImmediatePropagation();
                        } else {
                          getUserToSell(cell.row.original);
                        }
                      }}
                      type={cell.column.Header}
                      titleCell={cell.column.Header === 'Number'}
                      {...cell.getCellProps()}
                    >
                      {(function () {
                        switch (cell.column.Header) {
                          case 'Name':
                            return (
                              <StyledNameContainer>
                                <NameCell cell={cell} />
                              </StyledNameContainer>
                            );
                          case 'Potential':
                            return <PotentialCell cell={cell} />;
                          case 'Position':
                            return simplifiedPosition[cell.row.original.fav_position];
                          case 'Rating':
                            return <RatingCell cell={cell} />;
                          case 'Team':
                            return (
                              <StyledTeamCell>
                                <TeamOwnerIcon
                                  kind={clubOwnerKind({
                                    ...cell.row.original,
                                    botTeamIds,
                                  })}
                                  tooltipId={`search-team-${row.id}`}
                                />
                                {cell.row.original.teamName && cell.row.original.teamName !== 'Free agent' ? (
                                  <StyledUnderlineTeam>{cell.row.original.teamName}</StyledUnderlineTeam>
                                ) : (
                                  <span>Free agent</span>
                                )}
                              </StyledTeamCell>
                            );
                          case 'Actions':
                            return (
                              <ActionCell
                                name="signCell"
                                cell={cell}
                                openSignPopup={() => {
                                  if (!isAuthenticated) {
                                    goToDiscordLogin();
                                  } else {
                                    showSignModal(cell);
                                  }
                                }}
                                openLoanPopup={() => {
                                  if (!isAuthenticated) {
                                    goToDiscordLogin();
                                  } else {
                                    showLoanModal(cell);
                                  }
                                }}
                                openScoutPopup={() => {
                                  if (!isAuthenticated) {
                                    goToDiscordLogin();
                                  } else {
                                    showScoutModal(cell);
                                  }
                                }}
                              />
                            );
                          default:
                            return cell.render('Cell');
                        }
                      })()}
                    </StyledTd>
                  ))}
                </StyledTrTd>
              );
            })}
          </StyledTbody>
        </StyledTable>
      </StyledTableContainer>)}
      <StyledPagination>
        <StyledPageNumber>
          <StyledPaginationButton type="button" onClick={() => previousPage()} disabled={!canPreviousPage}>
            <ArrowLeft />
          </StyledPaginationButton>
          <StyledPageLabel>
            Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
          </StyledPageLabel>
          <StyledPaginationButton type="button" onClick={() => nextPage()} disabled={!canNextPage}>
            <ArrowRight />
          </StyledPaginationButton>
        </StyledPageNumber>
      </StyledPagination>
    </>
  );
});
